import { type LawPayload } from '#app/services/ai/types.ts'

export type GenericDocumentCitationTag = {
	type?: string
	id?: string
	href?: string
}

export type LooseLawDocumentReference = {
	lawNumber: string
	articles: number[]
}

export type LooseLawBracketCitation = {
	documentId: string
	articles: string[]
}

const DOCUMENT_TAG_PATTERN = /<document\b([^>]*)\/>|<document\b([^>]*)>\s*<\/document>/gi
const ATTRIBUTE_PATTERN = /([:\w-]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>/]+))/g
const MALFORMED_BRACKET_DOCUMENT_TAG_PATTERN = /\[\s*document\b([^\]\n]*?)\s*\/?\s*\]/gi
export const LOOSE_LAW_BRACKET_CITATION_PATTERN_SOURCE =
	String.raw`\[([^\]\n,]+),\s*Art(?:í|i)culos?\.?\s*([^\]\n]+)\]`
export const LOOSE_LAW_ID_BRACKET_CITATION_PATTERN_SOURCE =
	String.raw`\[(?:Ley\s+)?([A-Za-z0-9_]+(?:-[A-Za-z0-9_]+)+)\]`

export function normalizeMalformedDocumentCitationTags(text: string) {
	return text.replace(MALFORMED_BRACKET_DOCUMENT_TAG_PATTERN, (match, attributesText: string) => {
		const attributes = attributesText.trim().replace(/\/$/, '').trim()
		if (!/\b(?:id|href)\s*=/.test(attributes)) return match
		return attributes ? `<document ${attributes} />` : match
	})
}

export function parseGenericDocumentCitationTag(tag: string): GenericDocumentCitationTag | null {
	const normalizedTag = normalizeMalformedDocumentCitationTags(tag)
	const match = /^<document\b([\s\S]*?)(?:\/>|>)/i.exec(normalizedTag)
	if (!match) return null

	const attrs: GenericDocumentCitationTag = {}
	const attrText = match[1] ?? ''
	for (const attrMatch of attrText.matchAll(ATTRIBUTE_PATTERN)) {
		const name = attrMatch[1]?.toLowerCase()
		if (!name) continue
		const value = attrMatch[2] ?? attrMatch[3] ?? attrMatch[4] ?? ''
		if (name === 'type') attrs.type = value
		if (name === 'id') attrs.id = value
		if (name === 'href') attrs.href = value
	}

	return attrs.type || attrs.id || attrs.href ? attrs : null
}

export function extractGenericDocumentCitationTagsFromText(text: string | null | undefined) {
	if (!text) return [] as GenericDocumentCitationTag[]
	const tags: GenericDocumentCitationTag[] = []
	for (const match of normalizeMalformedDocumentCitationTags(text).matchAll(DOCUMENT_TAG_PATTERN)) {
		const parsed = parseGenericDocumentCitationTag(match[0])
		if (parsed) tags.push(parsed)
	}
	return tags
}

export function normalizeGenericDocumentType(type: string | undefined) {
	return (type ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[-_\s]+/g, '')
}

export function getGenericCostaRicaCitationType(type: string | undefined) {
	const normalizedType = normalizeGenericDocumentType(type)
	if (['ley', 'law', 'norma', 'normativa', 'codigo', 'codigocivil', 'codigocomercio'].includes(normalizedType)) {
		return 'Ley' as const
	}
	if (['resolucion', 'resoluciones', 'sentencia', 'jurisprudencia'].includes(normalizedType)) {
		return 'Resolucion' as const
	}
	if (['cgr', 'contraloria', 'contraloriageneral'].includes(normalizedType)) {
		return 'Cgr' as const
	}
	if (['tra', 'tribunalregistral'].includes(normalizedType)) {
		return 'Tra' as const
	}
	if (['gaceta', 'lagaceta'].includes(normalizedType)) return 'Gaceta' as const
	if (['boletinjudicial', 'boletin'].includes(normalizedType)) return 'BoletinJudicial' as const
	return null
}

function normalizeCitationText(text: string) {
	return text
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\s+/g, ' ')
		.trim()
}

export function parseLooseLawDocumentReference(documentId: string | null | undefined): LooseLawDocumentReference | null {
	const raw = (documentId ?? '').trim()
	if (!raw) return null

	const normalized = normalizeCitationText(raw)
	const lawNumber =
		/\bley\s*(?:n(?:o|umero)?\.?\s*)?(\d{2,6})\b/.exec(normalized)?.[1] ??
		/\bn(?:o|umero)?\.?\s*(\d{2,6})\b/.exec(normalized)?.[1] ??
		/^\s*(\d{2,6})[-\s]/.exec(raw)?.[1] ??
		/\b(\d{3,6})\b/.exec(raw)?.[1]

	if (!lawNumber) return null

	const articleSection =
		/\bart(?:iculo|iculos|s)?\.?\s*((?:\d+[a-z]?\s*(?:,|;|y|e|al|-)?\s*)+)/.exec(normalized)?.[1] ?? ''
	const articles = Array.from(new Set([...articleSection.matchAll(/\d+/g)].map(match => Number(match[0])))).filter(
		Number.isFinite,
	)

	return { lawNumber, articles }
}

export function parseLooseLawBracketCitationParts(
	documentId: string | null | undefined,
	articleText: string | null | undefined,
): LooseLawBracketCitation | null {
	const trimmedDocumentId = (documentId ?? '').trim()
	const trimmedArticleText = (articleText ?? '').trim()
	if (!trimmedDocumentId || !trimmedArticleText) return null

	const articles = Array.from(
		new Set(
			[...trimmedArticleText.matchAll(/\d+(?:\s*(?:bis|ter|qu[aá]ter|[a-z]))?/gi)]
				.map(match => match[0]?.replace(/\s+/g, ' ').trim())
				.filter(Boolean),
		),
	)
	if (articles.length === 0) return null

	return { documentId: trimmedDocumentId, articles }
}

export function parseLooseLawBracketCitation(citationText: string | null | undefined): LooseLawBracketCitation | null {
	const raw = (citationText ?? '').trim()
	if (!raw) return null

	const match = new RegExp(`^${LOOSE_LAW_BRACKET_CITATION_PATTERN_SOURCE}$`, 'i').exec(raw)
	if (!match) return null

	return parseLooseLawBracketCitationParts(match[1], match[2])
}

export function parseLooseLawIdBracketCitation(citationText: string | null | undefined) {
	const raw = (citationText ?? '').trim()
	if (!raw) return null

	const match = new RegExp(`^${LOOSE_LAW_ID_BRACKET_CITATION_PATTERN_SOURCE}$`, 'i').exec(raw)
	const documentId = match?.[1]?.trim()
	return documentId || null
}

export function formatLawArticleReferences(articles: string[]) {
	const uniqueArticles = Array.from(new Set(articles.map(article => article.trim()).filter(Boolean)))
	if (uniqueArticles.length === 0) return ''
	if (uniqueArticles.length === 1) return `Artículo ${uniqueArticles[0]}`
	if (uniqueArticles.length === 2) return `Artículos ${uniqueArticles[0]} y ${uniqueArticles[1]}`
	return `Artículos ${uniqueArticles.slice(0, -1).join(', ')} y ${uniqueArticles.at(-1)}`
}

export function normalizeLawArticleForMatch(articleNumber: string | number | null | undefined) {
	const article = String(articleNumber ?? '').trim()
	if (!article) return null
	const match = /\d+/.exec(article)
	return match ? String(Number(match[0])) : article.toLowerCase()
}

export function lawPayloadMatchesLooseReference(
	payload: Pick<LawPayload, 'number' | 'article_number' | 'idDocument'>,
	reference: LooseLawDocumentReference,
) {
	const payloadLawNumber = String(payload.number ?? '').trim()
	const idDocument = String(payload.idDocument ?? '')
	const matchesLawNumber = payloadLawNumber === reference.lawNumber || idDocument.startsWith(`${reference.lawNumber}-`)
	if (!matchesLawNumber) return false
	if (reference.articles.length === 0) return true
	const payloadArticle = normalizeLawArticleForMatch(payload.article_number)
	return Boolean(payloadArticle && reference.articles.map(String).includes(payloadArticle))
}
